import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { useMe } from "../hooks/useMe";

/**
 * userId → stored avatar value, for every user who's chosen a non-default one.
 *
 * An avatar is a global property of a user, not of any particular card/comment/
 * membership, so it's resolved here rather than threaded through every view. The
 * `Avatar` component — rendered all over with just a user id — reads this map,
 * which means no call site had to change to gain avatar support.
 */
const AvatarMapContext = createContext<Record<string, string>>({});

export function AvatarProvider({ children }: { children: ReactNode }) {
  const { me } = useMe();
  const { data } = useQuery({
    queryKey: ["avatars"],
    queryFn: () => api.users.avatars(),
    staleTime: 60_000,
  });

  const merged: Record<string, string> = { ...(data ?? {}) };
  // Reflect the current user's own choice immediately after they save it,
  // before the shared map is refetched (and clear it on reset to default).
  if (me) {
    if (me.avatar) merged[me.id] = me.avatar;
    else delete merged[me.id];
  }

  return (
    <AvatarMapContext.Provider value={merged}>
      {children}
    </AvatarMapContext.Provider>
  );
}

/** The stored avatar value for a user, or null to fall back to the identicon. */
export function useAvatar(userId: string): string | null {
  return useContext(AvatarMapContext)[userId] ?? null;
}
